import React from 'react'
import PropTypes from 'prop-types'
import { ErrorBoundary } from '@mc/error-boundary'
import { useExperiments } from './ExperimentContext'
import ExperimentLoader, { type Props as LoaderProps } from './ExperimentLoader'
import ExperimentTracker from './ExperimentTracker'
import ExperimentVisible from './ExperimentVisible'

type Props<TVariantProps> = {
  track?: boolean
  lazyload?: boolean
  defaultBucket?: string
  buffer?: number
} & Omit<LoaderProps<TVariantProps>, 'bucket'>

const Experiment = <TVariantProps,>({
  track = true,
  lazyload = false,
  defaultBucket = 'control',
  ...props
}: Props<TVariantProps>) => {
  const { experiments, experimentsVariables } = useExperiments()
  const { name } = props
  const bucket = experiments?.[name] || defaultBucket
  const variables = experimentsVariables?.[name] || {}

  const newProps = {
    ...props,
    bucket,
    variables,
  }

  let content = (
    <ExperimentLoader<TVariantProps>
      {...(newProps as LoaderProps<TVariantProps>)}
    />
  )

  if (lazyload) {
    content = <ExperimentVisible {...newProps}>{content}</ExperimentVisible>
  }

  if (track) {
    content = <ExperimentTracker {...newProps}>{content}</ExperimentTracker>
  }

  return <ErrorBoundary>{content}</ErrorBoundary>
}

Experiment.propTypes = {
  name: PropTypes.string.isRequired,
  lazyload: PropTypes.bool,
  track: PropTypes.bool,
  defaultBucket: PropTypes.string,
}

export default Experiment
